import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminMeQuery } from '../admin/shared/api'
import type { AdminRole } from '../admin/shared/permissions'
import { erpSettingsQuery } from './api'
import type { FrenteKey } from './contract'
import { frenteNav, frentesOf } from './frentes'

/* ERP · frente atual da pessoa (conveniência de tela: lembrada no navegador; sem armazenamento, a 1ª do perfil).
   Só vale uma frente que o perfil abre. Evento 'cf-frente' mantém menu e telas em sincronia. */

const KEY = 'cf-erp-frente'

function read(): string | null {
    try {
        return window.localStorage.getItem(KEY)
    } catch {
        return null
    }
}

export function currentFrente(role: AdminRole | null): FrenteKey {
    const allowed = role ? frentesOf(role) : (['gestao'] as const)
    const saved = read()
    return (allowed as readonly string[]).includes(saved ?? '') ? (saved as FrenteKey) : allowed[0]!
}

export function useFrente(role: AdminRole | null): [FrenteKey, (f: FrenteKey) => void] {
    const [frente, setState] = useState<FrenteKey>(() => currentFrente(role))
    useEffect(() => {
        const sync = () => setState(currentFrente(role))
        sync()
        window.addEventListener('cf-frente', sync)
        return () => window.removeEventListener('cf-frente', sync)
    }, [role])
    const set = useCallback((f: FrenteKey) => {
        try {
            window.localStorage.setItem(KEY, f)
        } catch {
            // sem armazenamento: vale só nesta página
        }
        setState(f)
        window.dispatchEvent(new Event('cf-frente'))
    }, [])
    return [frente, set]
}

/** Menu efetivo da pessoa no Staff: perfil, frente atual (e as que pode abrir) e as telas da frente
    (Configurações › Frentes e telas; se não carregar, o padrão). `nav` fica vazio enquanto o perfil carrega. */
export function useFrenteNav() {
    const role = useQuery(adminMeQuery).data?.role ?? null
    const [frente, setFrente] = useFrente(role)
    const settings = useQuery({ ...erpSettingsQuery, enabled: !!role, retry: false }).data ?? null
    const nav = useMemo(() => (role ? frenteNav(frente, role, settings) : []), [frente, role, settings])
    const frentes = role ? frentesOf(role) : []
    return { role, frente, setFrente, frentes, nav, settings }
}
